import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { cloneDeep, mergeDeep } from '../../utils/i18n';

const API = process.env.REACT_APP_API_URL || 'https://api.go2cdl.com';

const getToken = () =>
  (typeof localStorage !== 'undefined' && (
    localStorage.getItem('accessToken') || localStorage.getItem('go2cdl_access_token')
  )) || '';

const PreTripStatus = {
  NOT_STARTED: 'NOT_STARTED',
  IN_PROGRESS: 'IN_PROGRESS',
  PASSED: 'PASSED',
  FAILED: 'FAILED',
  NEEDS_REVIEW: 'NEEDS_REVIEW',
};

const DEFAULT_COPY = {
  loading: 'Loading training progress...',
  loadError: 'Failed to load training progress',
  updateError: 'Failed to update progress',
  notFound: 'No training progress found.',
  fields: {
    preTrip: {
      preTripInsideCab: 'Inside Cab',
      preTripBrakes: 'Brakes',
      preTripCoupling: 'Coupling',
      preTripTractorFront: 'Tractor Front',
      preTripTractorSide: 'Tractor Side',
      preTripTrailer: 'Trailer',
    },
    skills: { skill1: 'Skill 1', skill2: 'Skill 2', skill3: 'Skill 3', skill4: 'Skill 4' },
    road: { roadTest: 'Road Test' },
  },
  statuses: {
    NOT_STARTED: 'Not Started',
    IN_PROGRESS: 'In Progress',
    PASSED: 'Completed',
    FAILED: 'Failed',
    NEEDS_REVIEW: 'Needs Review',
    DONE: 'Completed',
  },
};

const normalizeStatus = (value) => {
  const upper = String(value || PreTripStatus.NOT_STARTED).toUpperCase();
  if (upper === 'DONE') return PreTripStatus.PASSED;
  return PreTripStatus[upper] ? upper : PreTripStatus.NOT_STARTED;
};

const normalizeProgressPayload = (payload) => {
  if (!payload) return payload;
  const fields = [
    'preTripInsideCab', 'preTripBrakes', 'preTripCoupling', 'preTripTractorFront',
    'preTripTractorSide', 'preTripTrailer', 'skill1', 'skill2', 'skill3', 'skill4', 'roadTest',
  ];
  return fields.reduce((normalized, field) => ({
    ...normalized,
    [field]: normalizeStatus(payload[field]),
  }), { ...payload });
};

const isCompleted = (value) => normalizeStatus(value) === PreTripStatus.PASSED;

const statusBadgeClass = (value) => {
  switch (normalizeStatus(value)) {
    case PreTripStatus.PASSED:
      return 'border-emerald-200 bg-emerald-50 text-emerald-700';
    case PreTripStatus.IN_PROGRESS:
      return 'border-amber-200 bg-amber-50 text-amber-700';
    case PreTripStatus.FAILED:
      return 'border-red-200 bg-red-50 text-red-700';
    case PreTripStatus.NEEDS_REVIEW:
      return 'border-orange-200 bg-orange-50 text-orange-700';
    default:
      return 'border-slate-200 bg-slate-50 text-slate-600';
  }
};

const getSelectClasses = (value) => {
  const status = normalizeStatus(value);
  if (status === PreTripStatus.PASSED) return 'border-emerald-300 bg-emerald-50 text-emerald-700 focus:ring-emerald-500';
  if (status === PreTripStatus.IN_PROGRESS) return 'border-amber-300 bg-amber-50 text-amber-700 focus:ring-amber-500';
  if (status === PreTripStatus.FAILED) return 'border-red-300 bg-red-50 text-red-700 focus:ring-red-500';
  if (status === PreTripStatus.NEEDS_REVIEW) return 'border-orange-300 bg-orange-50 text-orange-700 focus:ring-orange-500';
  return 'border-slate-300 bg-white text-slate-700 focus:ring-blue-500';
};

function ProgressBar({ value, muted = false, className = '' }) {
  return (
    <div className={`h-2 overflow-hidden rounded-full bg-slate-200 ${className}`}>
      <div
        className={`h-full rounded-full transition-all duration-300 ${muted ? 'bg-slate-300' : 'bg-[#1769ff]'}`}
        style={{ width: `${Math.max(0, Math.min(100, value))}%` }}
      />
    </div>
  );
}

function SectionIcon({ type }) {
  const common = { fill: 'none', stroke: 'currentColor', strokeWidth: '1.8', strokeLinecap: 'round', strokeLinejoin: 'round' };
  if (type === 'skills') {
    return <svg viewBox="0 0 48 48" aria-hidden="true" className="h-9 w-9"><circle cx="24" cy="24" r="15" {...common} /><circle cx="24" cy="24" r="6" {...common} /><path d="M24 9v9M24 30v9M9 24h9M30 24h9M13.4 13.4l6.4 6.4M28.2 28.2l6.4 6.4M34.6 13.4l-6.4 6.4M19.8 28.2l-6.4 6.4" {...common} /></svg>;
  }
  if (type === 'road') {
    return <svg viewBox="0 0 48 48" aria-hidden="true" className="h-9 w-9"><path d="M11 40 20 8h8l9 32" {...common} /><path d="M18 40h12M24 12v5m0 7v5m0 7v4" {...common} /></svg>;
  }
  return <svg viewBox="0 0 48 48" aria-hidden="true" className="h-9 w-9"><path d="M7 31h34v-12H31l-4-6H15l-4 6H7z" {...common} /><path d="M13 31v4m22-4v4M14 39a3 3 0 1 0 0-6 3 3 0 0 0 0 6Zm20 0a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" {...common} /><path d="M18 19h10" {...common} /></svg>;
}

function Chevron({ open }) {
  return <svg viewBox="0 0 24 24" aria-hidden="true" className={`h-5 w-5 shrink-0 text-slate-500 transition-transform ${open ? 'rotate-90' : ''}`}><path d="m9 18 6-6-6-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>;
}

function LockIcon() {
  return <svg viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5 text-slate-400"><rect x="5" y="10" width="14" height="10" rx="2" fill="none" stroke="currentColor" strokeWidth="1.8" /><path d="M8 10V7a4 4 0 0 1 8 0v3" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>;
}

export default function TrainingProgress({ userId, isAdmin = false, copy: copyOverrides, onClose }) {
  const copy = useMemo(() => mergeDeep(cloneDeep(DEFAULT_COPY), copyOverrides || {}), [copyOverrides]);
  const statusLabels = copy.statuses || DEFAULT_COPY.statuses;
  const statusOptions = useMemo(() => Object.values(PreTripStatus).map((value) => ({
    value,
    label: statusLabels[value] || DEFAULT_COPY.statuses[value],
  })), [statusLabels]);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState(null);

  const fetchProgress = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API}/api/training-progress/user/${userId}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed');
      const data = await response.json();
      if (!data.success || !data.progress) throw new Error('Failed');
      setProgress(normalizeProgressPayload(data.progress));
      setError(null);
    } catch (err) {
      console.error('Error fetching progress:', err);
      setError(copy.loadError);
    } finally {
      setLoading(false);
    }
  }, [copy.loadError, userId]);

  useEffect(() => { fetchProgress(); }, [fetchProgress]);

  const updateProgress = async (field, value) => {
    if (!isAdmin) return;
    try {
      setSaving(true);
      const response = await fetch(`${API}/api/training-progress/user/${userId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ [field]: value }),
      });
      if (!response.ok) throw new Error('Failed');
      const data = await response.json();
      setProgress(normalizeProgressPayload(data.progress));
    } catch (err) {
      console.error('Error updating progress:', err);
      alert(copy.updateError);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex min-h-[16rem] items-center justify-center p-8 text-slate-600">{copy.loading}</div>;
  if (error) return <div className="p-8 text-center text-red-600">{error}</div>;
  if (!progress) return <div className="p-8 text-center text-slate-600">{copy.notFound}</div>;

  const preTripFields = [
    { key: 'preTripInsideCab', label: copy.fields.preTrip.preTripInsideCab },
    { key: 'preTripBrakes', label: copy.fields.preTrip.preTripBrakes },
    { key: 'preTripCoupling', label: copy.fields.preTrip.preTripCoupling },
    { key: 'preTripTractorFront', label: copy.fields.preTrip.preTripTractorFront },
    { key: 'preTripTractorSide', label: copy.fields.preTrip.preTripTractorSide },
    { key: 'preTripTrailer', label: copy.fields.preTrip.preTripTrailer },
  ];
  const skillFields = [
    { key: 'skill1', label: copy.fields.skills.skill1 }, { key: 'skill2', label: copy.fields.skills.skill2 },
    { key: 'skill3', label: copy.fields.skills.skill3 }, { key: 'skill4', label: copy.fields.skills.skill4 },
  ];
  const roadTestFields = [{ key: 'roadTest', label: copy.fields.road.roadTest }];
  const getSectionMetrics = (fields) => {
    const complete = fields.filter(({ key }) => isCompleted(progress[key])).length;
    return { complete, total: fields.length, percent: Math.round((complete / fields.length) * 100) };
  };
  const preTrip = getSectionMetrics(preTripFields);
  const skills = getSectionMetrics(skillFields);
  const road = getSectionMetrics(roadTestFields);
  const totalActivities = preTrip.total + skills.total + road.total;
  const completedActivities = preTrip.complete + skills.complete + road.complete;
  const overallPercent = Math.round((completedActivities / totalActivities) * 100);
  const roadReady = preTrip.percent === 100 && skills.percent === 100;
  const sections = [
    { id: 'preTrip', title: 'Pre-Trip Inspection', icon: 'truck', fields: preTripFields, metrics: preTrip },
    { id: 'skills', title: 'Skills', icon: 'skills', fields: skillFields, metrics: skills },
    { id: 'road', title: 'Road Test', icon: 'road', fields: roadTestFields, metrics: road },
  ];
  const continueTraining = () => {
    const next = sections.find((section) => section.metrics.percent < 100 && (section.id !== 'road' || roadReady));
    if (next) setActiveSection(next.id);
  };

  return (
    <div className="min-h-full bg-slate-50 text-slate-900">
      <header className="sticky top-0 z-10 border-b border-slate-200 bg-white/95 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-6xl items-center px-4 sm:h-20 sm:px-7 lg:px-10">
          <img src="/images/logo.jpg" alt="Go2 CDL" className="h-10 w-20 object-contain sm:h-12 sm:w-24" />
        </div>
      </header>
      <main>
      <div className="mx-auto w-full max-w-6xl px-4 py-5 sm:px-7 sm:py-9 lg:px-10">
        <header className="mb-7 flex items-center justify-between gap-4">
          <h1 className="text-3xl font-bold tracking-tight text-[#061b45] sm:text-4xl">Training Progress</h1>
          {onClose && <button type="button" onClick={onClose} className="inline-flex shrink-0 items-center gap-2 rounded-2xl bg-slate-800 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-900" aria-label="Back to dashboard"><span className="text-lg leading-none" aria-hidden="true">←</span> Back</button>}
        </header>

        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-7">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <span className="text-5xl font-bold tracking-tight text-[#1769ff] sm:text-6xl">{overallPercent}%</span>
                <span className="text-2xl font-bold text-[#061b45] sm:text-3xl">Complete</span>
              </div>
              <p className="mt-1 text-sm text-slate-500 sm:text-base">{completedActivities} of {totalActivities} activities completed</p>
            </div>
            <button type="button" onClick={continueTraining} className="w-full rounded-xl bg-[#082c64] px-6 py-3.5 text-base font-semibold text-white shadow-sm transition hover:bg-[#0b397d] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 lg:w-auto">
              Continue Training
            </button>
          </div>
          <div className="mt-5 flex items-center gap-3">
            <ProgressBar value={overallPercent} className="flex-1" />
            <span className="text-sm font-semibold text-[#1769ff]">{overallPercent}%</span>
          </div>
        </section>

        <section className="mt-8">
          <h2 className="mb-3 text-xl font-bold text-[#061b45] sm:text-2xl">Training Sections</h2>
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            {sections.map((section, index) => {
              const locked = section.id === 'road' && !roadReady && !isAdmin;
              const open = activeSection === section.id;
              const subtitle = locked ? 'Available after training' : `${section.metrics.complete} of ${section.metrics.total} completed`;
              return (
                <div key={section.id} className={index ? 'border-t border-slate-200' : ''}>
                  <button
                    type="button"
                    disabled={locked}
                    aria-expanded={open}
                    onClick={() => !locked && setActiveSection(open ? null : section.id)}
                    className={`w-full px-4 py-4 text-left transition sm:px-6 sm:py-5 ${locked ? 'cursor-not-allowed' : 'hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500'}`}
                  >
                    <div className="flex items-center gap-3 sm:gap-5">
                      <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ${locked ? 'bg-slate-100 text-slate-400' : 'bg-slate-100 text-[#082c64]'}`}><SectionIcon type={section.icon} /></div>
                      <div className="min-w-0 flex-1">
                        <p className={`text-base font-bold sm:text-lg ${locked ? 'text-slate-500' : 'text-[#061b45]'}`}>{section.title}</p>
                        <p className="mt-0.5 text-sm text-slate-500">{subtitle}</p>
                      </div>
                      <div className="hidden w-48 items-center gap-3 md:flex lg:w-64">
                        <ProgressBar value={section.metrics.percent} muted={locked} className="flex-1" />
                        <span className={`w-11 text-right text-lg font-bold ${locked ? 'text-slate-400' : 'text-[#1769ff]'}`}>{locked ? '—' : `${section.metrics.percent}%`}</span>
                      </div>
                      {locked ? <LockIcon /> : <Chevron open={open} />}
                    </div>
                    <div className="ml-[60px] mt-3 flex items-center gap-3 md:hidden sm:ml-[68px]">
                      <ProgressBar value={section.metrics.percent} muted={locked} className="flex-1" />
                      <span className={`w-10 text-right text-sm font-bold ${locked ? 'text-slate-400' : 'text-[#1769ff]'}`}>{locked ? 'Locked' : `${section.metrics.percent}%`}</span>
                    </div>
                  </button>
                  {open && (
                    <div className="border-t border-slate-100 bg-slate-50 px-4 py-4 sm:px-6 sm:py-5">
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                        {section.fields.map((field) => {
                          const value = normalizeStatus(progress[field.key]);
                          return (
                            <div key={field.key} className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-3.5 py-3">
                              <span className="text-sm font-medium text-slate-700">{field.label}</span>
                              {isAdmin ? (
                                <select value={value} onChange={(event) => updateProgress(field.key, event.target.value)} disabled={saving} className={`rounded-lg border px-2 py-1.5 text-sm font-semibold outline-none focus:ring-2 ${getSelectClasses(value)}`}>
                                  {statusOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                                </select>
                              ) : (
                                <span className={`shrink-0 rounded-full border px-2.5 py-1 text-xs font-semibold ${statusBadgeClass(value)}`}>{statusLabels[value] || DEFAULT_COPY.statuses[value]}</span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <p className="mt-5 flex items-center gap-2 text-sm text-slate-500"><span className="flex h-5 w-5 items-center justify-center rounded-full border border-slate-400 text-xs font-bold">i</span> Select a section to view details.</p>
        </section>
      </div>
      </main>
    </div>
  );
}
